/**
 * Keycloak OIDC Authorization Code + PKCE (S256).
 * Mirrors services/admin-console/src/auth.ts for the agent-console client.
 */

import { flashToast, toastCopy } from '@/ui/toaster'
import { getAuthConfig, isOidcConfigured, oidcBase } from './config'

const KEY_TOKEN = 'w7_access_token'
const KEY_ID_TOKEN = 'w7_id_token'
const KEY_REFRESH = 'w7_refresh_token'
const KEY_VERIFIER = 'w7_pkce_verifier'
const KEY_STATE = 'w7_pkce_state'
const KEY_NEXT = 'w7_auth_next'
const KEY_DEMO = 'w7_demo_session'
/** Wall-clock ms when the current console session began (login / demo start). */
const KEY_SESSION_STARTED = 'w7_session_started_at'
/** Wall-clock ms of last pointer/keyboard/touch activity. */
const KEY_LAST_ACTIVITY = 'w7_session_last_activity'
/** Last Keycloak AIA started from the console (UPDATE_PASSWORD, CONFIGURE_TOTP, …). */
const KEY_KC_ACTION = 'w7_kc_action'

/** Absolute login lifetime — must match Keycloak ssoSessionMaxLifespan. */
export const SESSION_MAX_MS = 60 * 60 * 1000
/** Idle timeout — must match Keycloak ssoSessionIdleTimeout. */
export const SESSION_IDLE_MS = 30 * 60 * 1000

export interface Claims {
  sub: string
  agentId?: string
  /** Hierarchy depth from Keycloak attribute when mapped (1 = Company Master). */
  hierarchyLevel?: string
  roles: string[]
  preferredUsername?: string
  email?: string
  exp?: number
  /** Optional Keycloak attribute (Aggregate console gate). */
  accessLevel?: string
  /** Aggregate group id from Keycloak attribute / /v1/me. */
  aggregateGroupId?: string
  raw: Record<string, unknown>
}

function b64url(bytes: Uint8Array): string {
  let s = ''
  for (const b of bytes) s += String.fromCharCode(b)
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function rand(n: number): string {
  const a = new Uint8Array(n)
  crypto.getRandomValues(a)
  return b64url(a)
}

async function challenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return b64url(new Uint8Array(digest))
}

function redirectUri(): string {
  return `${location.origin}/callback`
}

export function getAccessToken(): string | null {
  return sessionStorage.getItem(KEY_TOKEN)
}

export function isDemoSession(): boolean {
  // Keycloak token wins: a stale demo flag must not block live APIs or show Mock chrome.
  if (getAccessToken()) {
    if (sessionStorage.getItem(KEY_DEMO) === '1') clearDemoSession()
    return false
  }
  return sessionStorage.getItem(KEY_DEMO) === '1'
}

export function startDemoSession(): void {
  clearOidcTokens()
  sessionStorage.setItem(KEY_DEMO, '1')
  markSessionStarted()
  touchSessionActivity()
}

export function clearDemoSession(): void {
  sessionStorage.removeItem(KEY_DEMO)
}

function clearOidcTokens(): void {
  sessionStorage.removeItem(KEY_TOKEN)
  sessionStorage.removeItem(KEY_ID_TOKEN)
  sessionStorage.removeItem(KEY_REFRESH)
  sessionStorage.removeItem(KEY_VERIFIER)
  sessionStorage.removeItem(KEY_STATE)
}

function clearSessionTimers(): void {
  sessionStorage.removeItem(KEY_SESSION_STARTED)
  sessionStorage.removeItem(KEY_LAST_ACTIVITY)
}

export function clearAllAuth(): void {
  clearOidcTokens()
  clearDemoSession()
  clearSessionTimers()
  sessionStorage.removeItem(KEY_NEXT)
  sessionStorage.removeItem(KEY_KC_ACTION)
  // Drop legacy Aggregate password-session profile if present.
  sessionStorage.removeItem('w7_aggregate_profile')
  callbackInFlight = null
  aiaResultLatch = null
  refreshInFlight = null
}

/** Record login/demo start (idempotent for an already-running session). */
export function markSessionStarted(): void {
  if (!sessionStorage.getItem(KEY_SESSION_STARTED)) {
    sessionStorage.setItem(KEY_SESSION_STARTED, String(Date.now()))
  }
}

/** Ensure a started-at stamp exists once tokens/demo are present. */
export function markSessionStartedIfNeeded(): void {
  if (getAccessToken() || isDemoSession()) markSessionStarted()
}

export function touchSessionActivity(): void {
  sessionStorage.setItem(KEY_LAST_ACTIVITY, String(Date.now()))
}

export function getSessionStartedAt(): number | null {
  const raw = sessionStorage.getItem(KEY_SESSION_STARTED)
  if (!raw) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

export function getLastActivityAt(): number | null {
  const raw = sessionStorage.getItem(KEY_LAST_ACTIVITY)
  if (!raw) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

export function sessionMaxExceeded(now = Date.now()): boolean {
  const started = getSessionStartedAt()
  if (started == null) return false
  return now - started >= SESSION_MAX_MS
}

export function sessionIdleExceeded(now = Date.now()): boolean {
  const last = getLastActivityAt() ?? getSessionStartedAt()
  if (last == null) return false
  return now - last >= SESSION_IDLE_MS
}

function getRefreshToken(): string | null {
  return sessionStorage.getItem(KEY_REFRESH)
}

let refreshInFlight: Promise<boolean> | null = null

/**
 * Exchange the refresh token for a new access token. Returns false when refresh
 * is unavailable or Keycloak rejects it (caller should treat as expired).
 */
export async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = (async () => {
    const cfg = getAuthConfig()
    const refresh = getRefreshToken()
    if (!refresh || !isOidcConfigured(cfg)) return false
    try {
      const response = await fetch(`${oidcBase(cfg)}/token`, {
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: cfg.clientId,
          refresh_token: refresh,
        }),
      })
      if (!response.ok) return false
      const json = (await response.json()) as {
        access_token?: string
        id_token?: string
        refresh_token?: string
      }
      if (!json.access_token) return false
      sessionStorage.setItem(KEY_TOKEN, json.access_token)
      if (json.id_token) sessionStorage.setItem(KEY_ID_TOKEN, json.id_token)
      if (json.refresh_token) sessionStorage.setItem(KEY_REFRESH, json.refresh_token)
      return true
    } catch {
      return false
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

export function decodeClaims(token: string | null): Claims | null {
  if (!token) return null
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const json = JSON.parse(
      decodeURIComponent(
        atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map((c) => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
          .join(''),
      ),
    ) as Record<string, unknown>
    const realmAccess = (json.realm_access ?? {}) as { roles?: string[] }
    const resource = (json.resource_access ?? {}) as Record<string, { roles?: string[] }>
    const clientRoles = resource['agent-console']?.roles ?? []
    const roles = [...new Set([...(realmAccess.roles ?? []), ...clientRoles])]
    return {
      sub: String(json.sub ?? ''),
      agentId: json.agentId ? String(json.agentId) : undefined,
      hierarchyLevel: json.hierarchyLevel != null ? String(json.hierarchyLevel) : undefined,
      roles,
      preferredUsername: json.preferred_username ? String(json.preferred_username) : undefined,
      email: json.email ? String(json.email) : undefined,
      exp: typeof json.exp === 'number' ? json.exp : undefined,
      accessLevel: json.accessLevel ? String(json.accessLevel) : undefined,
      aggregateGroupId: json.aggregateGroupId ? String(json.aggregateGroupId) : undefined,
      raw: json,
    }
  } catch {
    return null
  }
}

export function claims(): Claims | null {
  return decodeClaims(getAccessToken())
}

export function isAccessTokenExpired(c: Claims | null = claims()): boolean {
  if (!c?.exp) return false
  // 30s skew
  return Date.now() / 1000 >= c.exp - 30
}

/**
 * Aggregate-UI access after Keycloak: Aggregate groups only.
 * Requires realm role `aggregate` or accessLevel Aggregate. Agent/ops desks denied.
 */
export function hasConsoleAccess(c: Claims | null): boolean {
  if (!c) return false
  const roles = c.roles.map((r) => r.toLowerCase())
  return c.accessLevel === 'Aggregate' || roles.includes('aggregate')
}

/** Hierarchy leaf depth for Company Master — blocked from this console. */
export const COMPANY_MASTER_LEVEL = 1

/**
 * Live hierarchy check for console eligibility when JWT has no hierarchyLevel claim.
 * Returns `denied` for level 1 (CM); `unknown` if hierarchy cannot be reached.
 */
export async function resolveConsoleHierarchyGate(agentId: string): Promise<'ok' | 'denied' | 'unknown'> {
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim().replace(/\/$/, '')
  const apiBase = base && base.length > 0 ? base : import.meta.env.DEV ? '/api/hierarchy' : null
  if (!apiBase) return 'unknown'
  const token = getAccessToken()
  if (!token) return 'unknown'
  try {
    const res = await fetch(`${apiBase}/v1/agents/${encodeURIComponent(agentId)}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Caller-Id': agentId,
        Accept: 'application/json',
      },
    })
    if (!res.ok) return 'unknown'
    const body = (await res.json()) as { level?: number }
    if (typeof body.level !== 'number') return 'unknown'
    if (body.level === COMPANY_MASTER_LEVEL) return 'denied'
    return 'ok'
  } catch {
    return 'unknown'
  }
}

/**
 * Same-origin app paths only. Blocks `//host` and scheme-based open redirects
 * that would otherwise pass a naive `startsWith('/')` check.
 */
export function isSafeAppPath(path: string): boolean {
  if (!path.startsWith('/')) return false
  if (path.startsWith('//')) return false
  if (path.includes('\\')) return false
  try {
    const decoded = decodeURIComponent(path)
    if (decoded.startsWith('//') || decoded.includes('://')) return false
    if (decoded.includes('\\')) return false
  } catch {
    return false
  }
  return true
}

export function rememberNext(path: string): void {
  if (!path || path === '/login' || path === '/callback') return
  if (!isSafeAppPath(path)) return
  sessionStorage.setItem(KEY_NEXT, path)
}

export function consumeNext(fallback = '/overview'): string {
  const next = sessionStorage.getItem(KEY_NEXT) ?? fallback
  sessionStorage.removeItem(KEY_NEXT)
  return isSafeAppPath(next) ? next : fallback
}

export async function beginOidcLogin(loginHint?: string): Promise<void> {
  const cfg = getAuthConfig()
  if (!isOidcConfigured(cfg)) {
    throw new Error('Sign-in is not configured. Set VITE_KEYCLOAK_URL to enable Keycloak.')
  }
  // New login attempt — allow a fresh /callback exchange (SPA keeps module state).
  callbackInFlight = null
  clearDemoSession()
  const verifier = rand(48)
  const state = rand(16)
  sessionStorage.setItem(KEY_VERIFIER, verifier)
  sessionStorage.setItem(KEY_STATE, state)
  const params = new URLSearchParams({
    client_id: cfg.clientId,
    response_type: 'code',
    scope: 'openid profile email',
    redirect_uri: redirectUri(),
    state,
    code_challenge: await challenge(verifier),
    code_challenge_method: 'S256',
    // Force credentials / account picker — SSO after a denied console login
    // would otherwise re-issue a token for the same wrong-level user.
    prompt: 'login',
  })
  if (loginHint) params.set('login_hint', loginHint)
  location.assign(`${oidcBase(cfg)}/auth?${params}`)
}

export type KcActionStatus = 'success' | 'cancelled' | 'error' | null

let aiaResultLatch: { action: string | null; status: KcActionStatus } | null = null

/** Read + clear a pending Keycloak AIA + kc_action_status from the callback URL. */
export function consumeKcActionResult(url: URL = new URL(location.href)): {
  action: string | null
  status: KcActionStatus
} {
  // Strict Mode remounts /callback — latch once per page load like token exchange.
  if (aiaResultLatch) return aiaResultLatch
  const action = sessionStorage.getItem(KEY_KC_ACTION)
  sessionStorage.removeItem(KEY_KC_ACTION)
  const raw = url.searchParams.get('kc_action_status')
  const status: KcActionStatus =
    raw === 'success' || raw === 'cancelled' || raw === 'error' ? raw : null
  aiaResultLatch = { action, status }
  return aiaResultLatch
}

export type CallbackResult =
  | { ok: true }
  | { ok: false; reason: 'missing-code' | 'state' | 'token' | 'network' | 'not-configured' }

/**
 * React Strict Mode mounts /callback twice in dev. The auth code is single-use,
 * so a second token exchange fails. Share one in-flight promise per page load.
 */
let callbackInFlight: Promise<CallbackResult> | null = null

export function handleOidcCallback(): Promise<CallbackResult> {
  if (!callbackInFlight) callbackInFlight = runOidcCallback()
  return callbackInFlight
}

/** Test helper — clears the single-flight latch between cases. */
export function resetOidcCallbackLatch(): void {
  callbackInFlight = null
  aiaResultLatch = null
}

async function runOidcCallback(): Promise<CallbackResult> {
  const cfg = getAuthConfig()
  if (!isOidcConfigured(cfg)) return { ok: false, reason: 'not-configured' }

  // Remount after a successful first exchange (tokens already stored).
  if (getAccessToken() && !isAccessTokenExpired()) return { ok: true }

  const url = new URL(location.href)
  const err = url.searchParams.get('error')
  if (err) return { ok: false, reason: 'token' }

  const code = url.searchParams.get('code')
  const state = url.searchParams.get('state')
  if (!code) return { ok: false, reason: 'missing-code' }
  if (state !== sessionStorage.getItem(KEY_STATE)) return { ok: false, reason: 'state' }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: cfg.clientId,
    code,
    redirect_uri: redirectUri(),
    code_verifier: sessionStorage.getItem(KEY_VERIFIER) ?? '',
  })

  let response: Response
  try {
    response = await fetch(`${oidcBase(cfg)}/token`, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body,
    })
  } catch {
    return { ok: false, reason: 'network' }
  }

  if (!response.ok) return { ok: false, reason: 'token' }

  const json = (await response.json()) as {
    access_token?: string
    id_token?: string
    refresh_token?: string
  }
  if (!json.access_token) return { ok: false, reason: 'token' }

  sessionStorage.setItem(KEY_TOKEN, json.access_token)
  if (json.id_token) sessionStorage.setItem(KEY_ID_TOKEN, json.id_token)
  if (json.refresh_token) sessionStorage.setItem(KEY_REFRESH, json.refresh_token)
  sessionStorage.removeItem(KEY_VERIFIER)
  sessionStorage.removeItem(KEY_STATE)
  clearDemoSession()
  markSessionStarted()
  touchSessionActivity()
  return { ok: true }
}

export function logout(options?: { silent?: boolean }): void {
  const cfg = getAuthConfig()
  const idToken = sessionStorage.getItem(KEY_ID_TOKEN)
  const wasOidc = Boolean(getAccessToken())
  if (!options?.silent) flashToast(toastCopy.logoutSuccess)
  clearAllAuth()

  if (wasOidc && isOidcConfigured(cfg)) {
    const params = new URLSearchParams({
      client_id: cfg.clientId,
      post_logout_redirect_uri: `${location.origin}/login`,
    })
    if (idToken) params.set('id_token_hint', idToken)
    location.assign(`${oidcBase(cfg)}/logout?${params}`)
    return
  }
  location.assign('/login')
}

export type AuthPhase =
  | { phase: 'loading' }
  | { phase: 'anon' }
  | { phase: 'demo' }
  | { phase: 'no-access'; who: Claims }
  | { phase: 'ready'; who: Claims; mode: 'oidc' | 'demo' }
  | { phase: 'expired' }

export function resolveAuthPhase(): AuthPhase {
  if (isDemoSession()) {
    return { phase: 'ready', who: { sub: 'demo', roles: ['agent'], preferredUsername: 'demo', raw: {} }, mode: 'demo' }
  }
  const token = getAccessToken()
  if (!token) return { phase: 'anon' }
  const who = decodeClaims(token)
  if (!who) {
    clearOidcTokens()
    return { phase: 'anon' }
  }
  if (isAccessTokenExpired(who)) return { phase: 'expired' }
  if (!hasConsoleAccess(who)) return { phase: 'no-access', who }
  return { phase: 'ready', who, mode: 'oidc' }
}
